import {
  createInventorizationIssue,
  INVENTORIZATION_BLOCK_IDS,
  readInventorizationBlockStatus,
} from '@/features/inventorization/domain/inventorization';
import {
  FLOORPLAN_PLACEMENT_STATES,
  GPS_CAPTURE_STATES,
  type InventorizationPosition,
} from '@/features/inventorization/domain/inventorization-position';
import type { BlockStatus, ValidationIssue } from '@/shared/capture/capture-session';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';

export function validateInventorizationPosition(position: InventorizationPosition): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const blockId = INVENTORIZATION_BLOCK_IDS.position;

  if (!position.gpsState) {
    issues.push(
      createInventorizationIssue(
        blockId,
        'POSITION_GPS_STATE_MISSING',
        translateInventoryText('app.inventory.validation.position.gpsStateRequired', 'GPS state is required.'),
      ),
    );
  }

  if (
    position.gpsState === GPS_CAPTURE_STATES.captured &&
    !(hasNumber(position.gpsLat) && hasNumber(position.gpsLng))
  ) {
    issues.push(
      createInventorizationIssue(
        blockId,
        'POSITION_GPS_COORDINATES_MISSING',
        translateInventoryText(
          'app.inventory.validation.position.gpsCoordinatesRequired',
          'GPS coordinates are required.',
        ),
      ),
    );
  }

  if (position.gpsState === GPS_CAPTURE_STATES.unavailable && !position.gpsUnavailableReason.trim()) {
    issues.push(
      createInventorizationIssue(
        blockId,
        'POSITION_GPS_REASON_MISSING',
        translateInventoryText(
          'app.inventory.validation.position.gpsReasonRequired',
          'GPS unavailable reason is required.',
        ),
      ),
    );
  }

  if (!position.floorplanState) {
    issues.push(
      createInventorizationIssue(
        blockId,
        'POSITION_FLOORPLAN_STATE_MISSING',
        translateInventoryText(
          'app.inventory.validation.position.floorplanStateRequired',
          'Floorplan placement state is required.',
        ),
      ),
    );
  }

  if (
    position.floorplanState === FLOORPLAN_PLACEMENT_STATES.placed &&
    !(hasNumber(position.floorplanLat) && hasNumber(position.floorplanLng))
  ) {
    issues.push(
      createInventorizationIssue(
        blockId,
        'POSITION_FLOORPLAN_COORDINATES_MISSING',
        translateInventoryText(
          'app.inventory.validation.position.floorplanMarkerRequired',
          'Floorplan marker is required.',
        ),
      ),
    );
  }

  if (position.floorplanState === FLOORPLAN_PLACEMENT_STATES.notPlaced && !position.floorplanNotPlacedReason.trim()) {
    issues.push(
      createInventorizationIssue(
        blockId,
        'POSITION_FLOORPLAN_REASON_MISSING',
        translateInventoryText(
          'app.inventory.validation.position.floorplanReasonRequired',
          'Floorplan not-placed reason is required.',
        ),
      ),
    );
  }

  return issues;
}

export function getInventorizationPositionBlockStatus(position: InventorizationPosition): BlockStatus {
  return readInventorizationBlockStatus(validateInventorizationPosition(position));
}

function hasNumber(value: number | null) {
  return typeof value === 'number' && Number.isFinite(value);
}
